import React, { useState, useContext, useRef, useEffect } from 'react';
import axios from 'axios';
import { installRegisterSession } from '../utils/registerSession';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { TextField, Button, Typography, CircularProgress } from '@mui/material';
import './Login.css';
import TurnstileWidget from './TurnstileWidget';
import WorkspaceLoader from './WorkspaceLoader';
import { AUTH_APP_VERSION, AUTH_LEGAL_NOTICE } from '../constants/authMeta';

function Login() {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const turnstileRef = useRef();
  const [turnstileToken, setTurnstileToken] = useState('');
  const [twoFAToken, setTwoFAToken] = useState('');
  const location = useLocation();
  const [message, setMessage] = useState(location.state?.message || '');
  const [messageTone, setMessageTone] = useState('info');
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const is2FARequired = Boolean(mfaChallenge);
  const [isLoading, setIsLoading] = useState(false);
  const [isResendingConfirmation, setIsResendingConfirmation] = useState(false);
  const [canResendConfirmation, setCanResendConfirmation] = useState(false);
  const [loginComplete, setLoginComplete] = useState(false);
  const submitInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const attemptRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; attemptRef.current += 1; };
  }, []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const memberInvite = searchParams.get('memberInvite') || '';
  const { setUser } = useContext(UserContext);

  // ✅ Frontend bypass flag (dev/local)
  const TURNSTILE_BYPASS =
    process.env.REACT_APP_TURNSTILE_BYPASS === 'true' ||
    process.env.NODE_ENV === 'development';

  useEffect(() => {
    // Disable scroll on the login page (some browsers still allow scroll even with 100vh)
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyHeight = document.body.style.height;
    const prevHtmlHeight = document.documentElement.style.height;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.height = '100%';
    document.body.style.height = '100%';

    if (TURNSTILE_BYPASS) {
      setTurnstileToken('bypass-dev-token');
    }

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.height = prevBodyHeight;
      document.documentElement.style.height = prevHtmlHeight;
    };
  }, [TURNSTILE_BYPASS]);

  useEffect(() => {
    if (!mfaChallenge) return undefined;
    const timeout = window.setTimeout(() => {
      setMfaChallenge(null);
      setTwoFAToken('');
      setTurnstileToken(TURNSTILE_BYPASS ? 'bypass-dev-token' : '');
      setMessageTone('error');
      setMessage('Your sign-in attempt expired. Please enter your password and sign in again.');
    }, Math.max(0, mfaChallenge.expiresAt - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [mfaChallenge, TURNSTILE_BYPASS]);

  const resetTurnstile = () => {
    if (!TURNSTILE_BYPASS) {
      setTurnstileToken('');
      turnstileRef.current?.refresh();
    }
  };

  const restartLogin = () => {
    setMfaChallenge(null);
    setTwoFAToken('');
    setCredentials((previous) => ({ ...previous, password: '' }));
    setTurnstileToken(TURNSTILE_BYPASS ? 'bypass-dev-token' : '');
    setCanResendConfirmation(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    if (name === 'username') setCanResendConfirmation(false);
  };

  const handleResendConfirmation = async () => {
    const identifier = credentials.username.trim();
    if (!identifier || isResendingConfirmation) return;

    setIsResendingConfirmation(true);
    setMessageTone('info');
    setMessage('Requesting a new confirmation email...');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/resend-confirmation`,
        {
          identifier,
          turnstileToken: TURNSTILE_BYPASS ? undefined : turnstileToken,
        }
      );
      setMessageTone('info');
      setMessage(
        response.data?.message ||
          'If the account is still awaiting verification, a new confirmation link will be sent shortly.'
      );
    } catch (error) {
      setMessageTone('error');
      setMessage(error.response?.data?.message || 'We could not submit the request. Please check your connection and try again.');
    } finally {
      setIsResendingConfirmation(false);
      resetTurnstile();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading || isResendingConfirmation || submitInFlightRef.current) return;

    if (!is2FARequired && !TURNSTILE_BYPASS && !turnstileToken) {
      setMessageTone('error');
      setMessage('Please complete the security check before signing in.');
      return;
    }

    submitInFlightRef.current = true;
    const attempt = ++attemptRef.current;
    const startingToken = localStorage.getItem('token');
    const isCurrentAttempt = () => mountedRef.current && attemptRef.current === attempt && localStorage.getItem('token') === startingToken;
    setIsLoading(true);
    setCanResendConfirmation(false);
    setMessageTone('info');
    setMessage(is2FARequired ? 'Verifying 2FA code...' : 'Signing you in...');

    try {
      const response = is2FARequired
        ? await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login/2fa`, {
            challengeToken: mfaChallenge.token,
            code: twoFAToken,
          })
        : await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`, {
            ...credentials,
            turnstileToken: TURNSTILE_BYPASS ? undefined : turnstileToken,
          });

      if (!isCurrentAttempt()) return;
      if (!response.data.token) throw new Error('Missing access token.');
      const userResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/me`,
        { headers: { Authorization: `Bearer ${response.data.token}` } }
      );

      if (!isCurrentAttempt()) return;
      // Session installation remounts the authenticated workspace, so finish
      // navigation synchronously instead of relying on this page's timer.
      navigate(memberInvite ? `/register?memberInvite=${encodeURIComponent(memberInvite)}` : '/dashboard', { replace: true });
      installRegisterSession(response.data.token);
      setUser(userResponse.data);
      setMfaChallenge(null);
      setTwoFAToken('');
      setCredentials((previous) => ({ ...previous, password: '' }));
      setLoginComplete(true);
      setMessageTone('success');
      setMessage(memberInvite ? 'Login successful! Opening your invitation...' : 'Login successful! Redirecting to dashboard...');
    } catch (error) {
      if (!isCurrentAttempt()) return;
      const { status, data = {} } = error.response || {};
      if (!is2FARequired && status === 401 && data.code === '2FA_REQUIRED' && data.challengeToken) {
        setMfaChallenge({
          token: data.challengeToken,
          expiresAt: Date.now() + (Number(data.expiresIn) || 300) * 1000,
        });
        setCredentials((previous) => ({ ...previous, password: '' }));
        setTurnstileToken('');
        setTwoFAToken('');
        setMessageTone('info');
        setMessage(data.message || 'Please enter the code from your authenticator app.');
      } else {
        setMessageTone('error');
        if (is2FARequired && ['MFA_CHALLENGE_EXPIRED', 'MFA_CHALLENGE_INVALID', 'MFA_RATE_LIMITED'].includes(data.code)) {
          restartLogin();
        } else if (!is2FARequired) {
          // A Turnstile token can only be used once, including failed credential attempts.
          resetTurnstile();
        }

        if (status === 403 && data.code === 'EMAIL_NOT_VERIFIED') {
          setMessage('Please confirm your email address before logging in. Check your inbox for the EXREG confirmation email.');
          setCanResendConfirmation(true);
        } else if (status === 403 && data.code === 'DATABASE_EXPIRED') {
          setMessage('Your assigned database has expired. Please contact the administrator.');
        } else if (status === 400 && data.code === 'NO_DATABASE_ASSIGNED') {
          setMessage('No database assigned. Please contact the administrator.');
        } else {
          setMessage(data.message || (error.response
            ? 'Sign-in failed. Please try again.'
            : 'Network error. Please check your connection.'));
        }
      }
    } finally {
      submitInFlightRef.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  };

  if (loginComplete) {
    return <WorkspaceLoader message="Preparing your dashboard…" />;
  }

  return (
    <div className="login-container">
      <div className="login-shell">
        <div className="login-left">
          <div className="login-left-inner">
            <img src="/logo_white.png" alt="EXREG" className="login-brand-logo" />
            <Typography className="login-brand-subtitle">
              Hazardous Area Equipment Register & Inspection Platform
            </Typography>

            <ul className="login-highlights">
              <li>Offline mobile inspections + photo evidence</li>
              <li>Cloud register with role-based access</li>
              <li>ATEX / IECEx-ready reporting</li>
            </ul>

            <div className="login-left-badges">
              <span className="login-badge">Secure</span>
              <span className="login-badge">Audit-ready</span>
              <span className="login-badge">Fast rollout</span>
            </div>
          </div>
        </div>

        <div className="login-right">
          <div className="login-box">
            <Typography className="login-title">Sign in</Typography>
            <Typography className="login-subtitle">{is2FARequired ? 'Enter the code from your authenticator app.' : 'Use your EXREG account credentials.'}</Typography>

            <form onSubmit={handleSubmit} className="login-form">
              <TextField
                label="Username"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                margin="normal"
                autoComplete="username"
                disabled={isLoading || is2FARequired}
              />
              {!is2FARequired && <TextField
                label="Password"
                name="password"
                type="password"
                value={credentials.password}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                required
                margin="normal"
                autoComplete="current-password"
                disabled={isLoading}
              />}

              {is2FARequired && (
                <TextField
                  label="2FA Code"
                  value={twoFAToken}
                  onChange={(e) => setTwoFAToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  variant="outlined"
                  fullWidth
                  required
                  margin="normal"
                  autoComplete="one-time-code"
                  autoFocus
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 }}
                  disabled={isLoading}
                />
              )}

              {!TURNSTILE_BYPASS && !is2FARequired && (
                <div className="login-turnstile">
                  <TurnstileWidget
                    ref={turnstileRef}
                    onVerify={setTurnstileToken}
                  />
                </div>
              )}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                className="login-primary-btn"
                disabled={isLoading || isResendingConfirmation || (is2FARequired ? twoFAToken.length !== 6 : (!TURNSTILE_BYPASS && !turnstileToken))}
              >
                {isLoading ? (
                  <span className="login-button-loading">
                    <CircularProgress size={18} thickness={5} className="login-button-spinner" />
                    <span>{is2FARequired ? 'Verifying...' : 'Signing in...'}</span>
                  </span>
                ) : (
                  is2FARequired ? 'Verify code' : 'Login'
                )}
              </Button>

              {is2FARequired && (
                <Button
                  type="button"
                  variant="text"
                  fullWidth
                  disabled={isLoading}
                  onClick={() => {
                    restartLogin();
                    setMessageTone('info');
                    setMessage('Enter your credentials to start a new sign-in attempt.');
                  }}
                >
                  Start again
                </Button>
              )}

              {!is2FARequired && canResendConfirmation && (
                <Button
                  type="button"
                  variant="text"
                  color="primary"
                  fullWidth
                  className="login-forgot-btn"
                  onClick={handleResendConfirmation}
                  disabled={
                    isLoading ||
                    isResendingConfirmation ||
                    (!TURNSTILE_BYPASS && !turnstileToken)
                  }
                >
                  {isResendingConfirmation ? 'Requesting email...' : 'Resend confirmation email'}
                </Button>
              )}

              <Button
                variant="text"
                color="secondary"
                fullWidth
                className="login-forgot-btn"
                onClick={() => navigate('/forgot-password')}
                disabled={isLoading}
              >
                Forgot password?
              </Button>

              <Button
                variant="text"
                color="secondary"
                fullWidth
                className="login-forgot-btn"
                onClick={() => navigate('/register')}
                disabled={isLoading}
              >
                Don’t have an account? Register
              </Button>
            </form>

            {message && (
              <Typography
                variant="body2"
                className={`login-message ${messageTone}`}
                role={messageTone === 'error' ? 'alert' : 'status'}
              >
                {message}
              </Typography>
            )}

            <div className="login-meta" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="caption" className="login-version" style={{ textAlign: 'center' }}>{AUTH_APP_VERSION}</Typography>
              <Typography variant="caption" className="login-legal" style={{ textAlign: 'center' }}>
                {AUTH_LEGAL_NOTICE}
              </Typography>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
